import { useState, useEffect } from 'react'
import { ResponsiveBar } from '@nivo/bar'
import s from './OpportunityByLostDate.scss'
import { makeReportsPostRequest, makeGetRequest, getReportsBaseUrl } from 'utils/api'
import { toTeams } from 'utils/serializers'
import { toBarChartData, toUsers, getColors } from './serializers'
import { useLoading } from 'hooks'
import { getCurrency, getToken } from 'utils/authService'
import MaterialTable from '@material-table/core'
import { NumericFormat } from 'react-number-format'
import { RangeDatePicker } from 'components/ideal-core-ui'
import { utcDate, timezoneDate, currencyFormatter } from 'utils/helpers'
import isEmpty from 'lodash/isEmpty'
import Select from 'react-select'
import { FaInfoCircle, FaFileExcel, FaAngleRight, FaAngleDown } from 'react-icons/fa'
import { Card, CardBody, CardHeader, Collapse, UncontrolledTooltip } from 'reactstrap'
import Cookies from 'universal-cookie'
import { COOKIES } from 'utils/constants'
import moment from 'moment'
import currency from 'currency.js'
import { AddButton } from 'components/common/AddButton'
import { ExportCsv, ExportPdf } from '@material-table/exporters'

const columns = [
  {
    title: 'Lost Date',
    field: 'lostOnDate',
    sorting: true,
    width: '200px',
    defaultSort: 'asc',
    render: row => (row.lostOnDate ? timezoneDate(row.lostOnDate) : null),
    exportTransformer: row => (row.lostOnDate ? timezoneDate(row.lostOnDate) : null)
  },
  {
    title: 'Bid Value',
    field: 'value',
    width: '200px',
    sorting: true,
    render: row => (
      <NumericFormat
        thousandSeparator
        decimalScale={2}
        value={row.value}
        prefix={getCurrency().symbol}
        displayType="text"
      />
    ),
    exportTransformer: row => currencyFormatter({ value: row.value })
  },
  {
    title: 'Submission Date',
    field: 'bidSubmissionDate',
    sorting: true,
    width: '200px',
    defaultSort: 'asc',
    render: row => (row.bidSubmissionDate ? timezoneDate(row.bidSubmissionDate) : null),
    exportTransformer: row => (row.bidSubmissionDate ? timezoneDate(row.bidSubmissionDate) : null)
  },
  {
    title: 'Bid Due Date',
    field: 'bidDueDate',
    sorting: true,
    width: '200px',
    render: row => timezoneDate(row.bidDueDate),
    exportTransformer: row => timezoneDate(row.bidDueDate)
  },
  {
    title: 'Bid Follow-up Date',
    field: 'bidFollowUpDate',
    sorting: true,
    width: '200px',
    render: row => (row.bidFollowUpDate ? timezoneDate(row.bidFollowUpDate) : null),
    exportTransformer: row => (row.bidFollowUpDate ? timezoneDate(row.bidFollowUpDate) : null)
  },
  {
    title: 'Quote #',
    field: 'quoteNumber',
    sorting: true,
    width: '200px'
  },
  {
    title: 'Project #',
    field: 'projectNumber',
    width: '200px',
    sorting: true
  },
  {
    title: 'Opp Status',
    field: 'status',
    width: '200px',
    sorting: true
  },
  {
    title: 'Opp Name',
    field: 'name',
    width: '200px',
    sorting: true,
    render: row => (
      <a href={`/opportunities/${row.id}`} target="_blank" rel="noreferrer">
        {row.name}
      </a>
    )
  },
  {
    title: 'Client(s)',
    field: 'companies',
    sorting: true,
    width: '200px',
    render: row => row.companies.join(', '),
    exportTransformer: row => row.companies.join(', ')
  },
  {
    title: 'Contact(s)',
    field: 'contacts',
    sorting: true,
    width: '400px',
    render: row => {
      return row.contacts
        .map(
          c =>
            `${c.company}, ${c.name}, ${c.phone ? c.phone + ', ' : ''}${
              c.cellPhone ? c.cellPhone + ', ' : ''
            }${c.email}`
        )
        .join(' | ')
    },
    exportTransformer: row => {
      return row.contacts
        .map(
          c =>
            `${c.company}, ${c.name}, ${c.phone ? c.phone + ', ' : ''}${
              c.cellPhone ? c.cellPhone + ', ' : ''
            }${c.email}`
        )
        .join(' | ')
    }
  },
  {
    title: 'Awarded To',
    field: 'awardedCompany',
    width: '200px',
    sorting: true
  },
  {
    title: 'Cost',
    field: 'cost',
    width: '200px',
    sorting: true,
    render: row => (
      <NumericFormat
        thousandSeparator
        decimalScale={2}
        value={row.cost}
        prefix={getCurrency().symbol}
        displayType="text"
      />
    ),
    exportTransformer: row => (row.cost ? currencyFormatter({ value: row.cost }) : '')
  },
  {
    title: 'Profit',
    field: 'grossProfit',
    width: '200px',
    sorting: true,
    render: row => (
      <NumericFormat
        thousandSeparator
        decimalScale={2}
        value={row.grossProfit}
        prefix={getCurrency().symbol}
        displayType="text"
      />
    ),
    exportTransformer: row => currencyFormatter({ value: row.grossProfit })
  },
  {
    title: 'Team(s)',
    field: 'teams',
    width: '250px',
    sorting: true,
    render: row => row.teams?.join(', '),
    exportTransformer: row => row.teams?.join(', ')
  },
  {
    title: 'Opp Manager',
    field: 'manager',
    width: '200px',
    sorting: true
  }
]

const cookies = new Cookies()

const OpportunityByLostDate = () => {
  const [pipelineInfo, setPipelineInfo] = useState(null)
  const [result, setResult] = useState([])
  const [users, setUsers] = useState([])
  const [userFilter, setUserFilter] = useState([])
  const [teamFilter, setTeamFilter] = useState([])
  const [teams, setTeams] = useState([])
  const [, withLoadingLocks] = useLoading()
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [collapse, setCollapse] = useState('')

  const setDateFilters = (dates, utc = true) => {
    setStartDate(utc ? utcDate(dates.from) : dates.from)
    setEndDate(utc ? utcDate(dates.to) : dates.to)
  }

  useEffect(() => {
    const sessionDateRangeFilters = cookies.get(COOKIES.DATE_RANGE_FILTERS)
    if (sessionDateRangeFilters?.startDate) {
      setDateFilters(
        {
          from: sessionDateRangeFilters.startDate,
          to: sessionDateRangeFilters.endDate
        },
        false
      )
    } else {
      setDateFilters({
        from: moment().subtract(2, 'weeks').startOf('day').format(),
        to: moment().add(6, 'weeks').endOf('day').format()
      })
    }
  }, [])

  useEffect(() => {
    ;(async () => {
      const { content } = await makeGetRequest('/v1/users')
      setUsers(toUsers(content))
    })()
  }, [])

  useEffect(() => {
    ;(async () => {
      const teams = await makeGetRequest('/v1/teams')
      setTeams(toTeams(teams))
    })()
  }, [])

  useEffect(() => {
    ;(async () => {
      if (!startDate || !endDate) return
      const result = await withLoadingLocks(
        makeReportsPostRequest('/v1/deals/lostOnDate', {
          from: startDate,
          to: endDate,
          ...(userFilter && userFilter.length
            ? { userFilter: userFilter.map(user => user.value) }
            : {}),
          ...(teamFilter && teamFilter.length
            ? { teamFilter: teamFilter.map(user => user.value) }
            : {})
        })
      )
      setResult(result)
      setPipelineInfo(toBarChartData(result))
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, teamFilter, userFilter])

  const onBtnExport = () => {
    window.open(
      getReportsBaseUrl(
        `/v1/deals/lostOnDate/export?from=${startDate}&to=${endDate}&userFilter=${userFilter
          .map(user => user.value)
          .join()}&teamFilter=${teamFilter.map(user => user.value).join()}&token=${getToken()}`
      ),
      '_blank'
    )
  }

  const toggleCollapse = month => {
    if (collapse !== month) {
      setCollapse(month)
    } else {
      setCollapse('')
    }
  }

  return (
    <>
      <h6 className={s.title}>Sales Reports &gt; Opportunities by Lost Date</h6>
      <div className={s.filters}>
        <RangeDatePicker startDate={startDate} endDate={endDate} onChange={setDateFilters} />
        <Select
          onChange={values => setUserFilter(values)}
          options={users}
          value={userFilter}
          placeholder="Select User(s)"
          className={s.userSelect}
          isMulti
        />
        <FaInfoCircle id="Type1" style={{ position: 'relative', top: 15, left: 5 }} />
        <UncontrolledTooltip placement="bottom" target="Type1">
          To see all opportunities, please remove all users from selection.
        </UncontrolledTooltip>
        <Select
          onChange={values => setTeamFilter(values)}
          options={teams}
          value={teamFilter}
          placeholder="Select Team(s)"
          className={s.userSelect}
          isMulti
        />
        <FaInfoCircle id="Type2" style={{ position: 'relative', top: 15, left: 5 }} />
        <UncontrolledTooltip placement="bottom" target="Type2">
          To see all opportunities, please remove all teams from selection.
        </UncontrolledTooltip>
      </div>
      {Object.keys(result).length > 0 && !isEmpty(pipelineInfo) ? (
        <>
          <h6 className={s.subtitle}>
            Total: {pipelineInfo?.totalCount ?? 0} opportunities &#8226; Lost:{' '}
            {currency(pipelineInfo?.totalBidValue ?? 0, { symbol: getCurrency().symbol }).format()}
          </h6>
          <div
            className={s.barChart}
            style={{ height: pipelineInfo.barByValue.length * 60 + 'px', maxHeight: 800 }}
          >
            <ResponsiveBar
              colors={getColors}
              data={pipelineInfo.barByValue}
              keys={pipelineInfo.keys}
              indexBy="month"
              enableGridX
              enableGridY
              margin={{ top: 10, right: 100, bottom: 50, left: 100 }}
              padding={0.2}
              innerPadding={3}
              groupMode="grouped"
              borderColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
              axisTop={null}
              axisRight={null}
              axisBottom={{
                tickSize: 5,
                tickPadding: 5,
                tickRotation: 0,
                legendPosition: 'middle',
                legendOffset: 32
              }}
              axisLeft={{
                tickSize: 0,
                tickPadding: 5,
                tickRotation: 0,
                legendPosition: 'middle',
                legendOffset: -40,
                format: value => currency(value, { symbol: getCurrency().symbol }).format()
              }}
              tooltip={({ id, value }) => (
                <span>
                  {id}:{' '}
                  <strong>
                    <NumericFormat
                      thousandSeparator
                      decimalScale={2}
                      value={value}
                      prefix={getCurrency().symbol}
                      displayType="text"
                    />
                  </strong>
                </span>
              )}
              enableLabel={false}
              labelSkipWidth={12}
              labelSkipHeight={9}
              labelTextColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
              legends={[
                {
                  dataFrom: 'keys',
                  anchor: 'bottom-right',
                  direction: 'column',
                  justify: false,
                  translateX: 120,
                  translateY: 0,
                  itemsSpacing: 14,
                  itemWidth: 100,
                  itemHeight: 20,
                  itemDirection: 'left-to-right',
                  itemOpacity: 0.85,
                  symbolSize: 20,
                  effects: [
                    {
                      on: 'hover',
                      style: {
                        itemOpacity: 1
                      }
                    }
                  ]
                }
              ]}
              animate
              motionStiffness={90}
              motionDamping={15}
            />
          </div>
          <div className={s.table}>
            <AddButton className={s.exportExcel} onClick={onBtnExport}>
              <FaFileExcel className={s.excelIcon} />
              Export to Excel
            </AddButton>
            {Object.keys(result).map(month => (
              <Card className={s.card} key={month}>
                <CardHeader onClick={() => toggleCollapse(month)} data-event={month}>
                  <span className={s.arrow}>
                    {collapse === month ? <FaAngleRight /> : <FaAngleDown />}
                  </span>
                  {month} ({result[month].count}) &#8226; Lost:{' '}
                  {currency(result[month].totalBidValue, {
                    symbol: getCurrency().symbol
                  }).format()}{' '}
                </CardHeader>
                <Collapse isOpen={collapse === month}>
                  <CardBody>
                    {collapse === month && (
                      <MaterialTable
                        title=""
                        key={month}
                        columns={columns}
                        data={result[month].deals}
                        options={{
                          tableLayout: 'fixed',
                          sorting: true,
                          headerStyle: {
                            backgroundColor: '#eee',
                            color: '#000',
                            fontSize: '14px'
                          },
                          pageSize: 10,
                          padding: 'dense',
                          pageSizeOptions: [
                            5,
                            10,
                            20,
                            50,
                            { value: result[month].deals.length, label: 'All' }
                          ],
                          emptyRowsWhenPaging: false,
                          exportMenu: [
                            {
                              label: 'Export PDF',
                              exportFunc: (cols, datas) =>
                                ExportPdf(cols, datas, 'Opportunities by Lost Date')
                            },
                            {
                              label: 'Export CSV',
                              exportFunc: (cols, datas) =>
                                ExportCsv(cols, datas, 'Opportunities by Lost Date')
                            }
                          ],
                          columnsButton: true
                        }}
                        style={{
                          boxShadow: 'none',
                          fontSize: '13px'
                        }}
                      />
                    )}
                  </CardBody>
                </Collapse>
              </Card>
            ))}
          </div>
        </>
      ) : (
        <p className={s.noResults}>No results to display. Try different filters.</p>
      )}
    </>
  )
}

export default OpportunityByLostDate
